/** i18n leaf keys of the three unsupported-message labels (same names under NOTIFICATION_MSG). */
export const UNSUPPORTED_LABEL_KEYS = [
  "UNSUPPORTED_MESSAGE",
  "UNSUPPORTED_MESSAGE_APP_ONLY",
  "UNSUPPORTED_MESSAGE_TYPE",
] as const;
export type UnsupportedLabelKey = (typeof UNSUPPORTED_LABEL_KEYS)[number];

/** For a browser still holding translation files older than these keys. */
export const UNSUPPORTED_FALLBACK_LABELS: Record<UnsupportedLabelKey, string> = {
  UNSUPPORTED_MESSAGE: "Message non pris en charge",
  UNSUPPORTED_MESSAGE_APP_ONLY:
    "Message non disponible : consultez-le sur l'application WhatsApp Business",
  UNSUPPORTED_MESSAGE_TYPE: "Type de message non pris en charge par WhatsApp",
};

const UNSUPPORTED_I18N_PREFIX = "TEAM_INBOX.CONVERSATION.";
const UNSUPPORTED_MARKER_PREFIX = /^UNKNOWN\s*:/;
const UNSUPPORTED_MARKER = /^UNKNOWN\s*:\s*(\d+)?(?:\|([\w-]{1,23}))?$/;
const UNSUPPORTED_TYPE = /^[\w-]{1,23}$/;
const CODE_APP_ONLY = 131060;
const CODE_UNSUPPORTED_TYPE = 131051;

/** A validated Meta error code and message type. Nothing else from the payload is ever shown. */
export interface UnsupportedInfo {
  code?: number;
  type?: string;
}

export interface UnsupportedLabelParts {
  key: UnsupportedLabelKey;
  /** Appended to the translated label. Empty: the type name is too technical for clients. */
  suffix: string;
}

function validUnsupportedInfo(code: unknown, type: unknown): UnsupportedInfo {
  const info: UnsupportedInfo = {};
  if (typeof code === "number" && Number.isInteger(code) && code > 0) {
    info.code = code;
  }
  if (typeof type === "string" && UNSUPPORTED_TYPE.test(type) && type.toLowerCase() !== "unknown") {
    info.type = type;
  }
  return info;
}

/** True for the waba marker and old `UNKNOWN : ...` rows. Case-sensitive; a bare `UNKNOWN` is text. */
export function isUnsupportedMarker(text: string | undefined | null): boolean {
  return UNSUPPORTED_MARKER_PREFIX.test((text ?? "").trim());
}

/** `UNKNOWN : 131051|poll` into its code and type. Any other text carries neither. */
export function parseUnsupportedMarker(text: string | undefined | null): UnsupportedInfo {
  const match = UNSUPPORTED_MARKER.exec((text ?? "").trim());
  if (!match) return {};
  return validUnsupportedInfo(match[1] ? Number(match[1]) : undefined, match[2]);
}

/** Reads `message.jsonOptions`, which is untrusted: only a numeric code and a plain type survive. */
export function parseUnsupportedOptions(jsonOptions: unknown): UnsupportedInfo {
  if (typeof jsonOptions !== "string" || !jsonOptions) return {};
  let unsupported: { type?: unknown; errors?: unknown } | null | undefined;
  try {
    unsupported = JSON.parse(jsonOptions)?.unsupported;
  } catch {
    return {};
  }
  if (!unsupported || typeof unsupported !== "object") return {};
  const codes = (Array.isArray(unsupported.errors) ? unsupported.errors : [])
    .map((error: { code?: unknown } | null) => error?.code)
    .filter((code: unknown) => validUnsupportedInfo(code, undefined).code !== undefined) as number[];
  const code = codes.includes(CODE_APP_ONLY) ? CODE_APP_ONLY : codes[0];
  return validUnsupportedInfo(code, unsupported.type);
}

/** The marker waba would store for this code and type, for rows built from a live message. */
export function buildUnsupportedMarker({ code, type }: UnsupportedInfo): string {
  const valid = validUnsupportedInfo(code, type);
  return `UNKNOWN : ${valid.code ?? ""}${valid.type ? `|${valid.type}` : ""}`;
}

/** The label rule, as a key and a suffix so templates can keep the translate pipe. */
export function unsupportedLabelParts(code: unknown, type: unknown): UnsupportedLabelParts {
  const valid = validUnsupportedInfo(code, type);
  if (valid.code === CODE_APP_ONLY) {
    return { key: "UNSUPPORTED_MESSAGE_APP_ONLY", suffix: "" };
  }
  if (valid.type || valid.code === CODE_UNSUPPORTED_TYPE) {
    return { key: "UNSUPPORTED_MESSAGE_TYPE", suffix: "" };
  }
  return { key: "UNSUPPORTED_MESSAGE", suffix: "" };
}

/** The full i18n key of a label, for the translate pipe. */
export function unsupportedI18nKey(key: UnsupportedLabelKey): string {
  return UNSUPPORTED_I18N_PREFIX + key;
}

/** The final text from an already translated key: the hardcoded label if it came back untranslated. */
export function unsupportedLabelText(parts: UnsupportedLabelParts, translated: string | undefined | null): string {
  const label =
    translated && translated !== unsupportedI18nKey(parts.key)
      ? translated
      : UNSUPPORTED_FALLBACK_LABELS[parts.key];
  return label + parts.suffix;
}

export function unsupportedLabel(code: unknown, type: unknown, translate: (key: string) => string): string {
  const parts = unsupportedLabelParts(code, type);
  return unsupportedLabelText(parts, translate(unsupportedI18nKey(parts.key)));
}

/**
 * Formats conversation last-message previews for lists/modals.
 * Typed payloads like `CONTACTS: {...}` become a short label instead of raw JSON.
 */
export function formatLastMessagePreview(
  msg: string | undefined | null,
  translate: (key: string) => string,
  maxLen = 40,
): string {
  const raw = (msg || "").trim();
  if (!raw) {
    return "-";
  }

  // On the original string: the switch below upper-cases, and this rule is case-sensitive.
  if (isUnsupportedMarker(raw)) {
    const { code, type: messageType } = parseUnsupportedMarker(raw);
    return unsupportedLabel(code, messageType, translate);
  }

  const type = raw.split(":")[0]?.trim().toUpperCase();
  switch (type) {
    case "CONTACTS":
      return translate("TEAM_INBOX.CONVERSATION.CONTACT");
    case "LOCATION":
      return translate("TEAM_INBOX.CONVERSATION.LOCATION");
    case "IMAGE":
      return translate("TEAM_INBOX.CONVERSATION.IMAGE");
    case "AUDIO":
      return translate("TEAM_INBOX.CONVERSATION.AUDIO");
    case "VIDEO":
      return translate("TEAM_INBOX.CONVERSATION.VIDEO");
    case "DOCUMENT":
      return translate("TEAM_INBOX.CONVERSATION.DOCUMENT");
    case "STICKER":
      return translate("TEAM_INBOX.CONVERSATION.STICKER");
    case "INTERACTIVE": {
      const separatorIndex = raw.indexOf(":");
      const content =
        separatorIndex >= 0 ? raw.slice(separatorIndex + 1).trim() : "";
      if (!content || content.startsWith("{") || content.startsWith("[")) {
        return translate("FLOW_META.PAGE.RESPONSE");
      }
      const decodedReplyCode = decodeInteractiveReplyCode(content, translate);
      if (decodedReplyCode) {
        return decodedReplyCode;
      }
      const cleaned = content.replace(/\s+/g, " ").trim();
      return cleaned.length <= maxLen
        ? cleaned
        : `${cleaned.slice(0, maxLen).trimEnd()}…`;
    }
    case "ORDER":
      return translate("ORDER_META.PAGE.RESPONSE");
    case "REACTION": {
      const content = raw.slice(raw.indexOf(":") + 1).trim();
      return `${translate("TEAM_INBOX.CONVERSATION.REACTION_MASSAGE")} ${content}`.trim();
    }
    default:
      break;
  }

  const decodedReplyCode = decodeInteractiveReplyCode(raw, translate);
  if (decodedReplyCode) {
    return decodedReplyCode;
  }

  // Avoid dumping JSON blobs in previews.
  if (raw.startsWith("{") || raw.startsWith("[")) {
    return translate("TEAM_INBOX.CONVERSATION.INTERACTIVE_MESSAGE");
  }

  const cleaned = raw.replace(/\s+/g, " ").trim();
  if (cleaned.length <= maxLen) {
    return cleaned;
  }
  return `${cleaned.slice(0, maxLen).trimEnd()}…`;
}

/**
 * Decodes the raw button/list-reply payloads carousels send back
 * (e.g. `CAT:110:4687828735`) into a human-readable label. Returns
 * `undefined` when `raw` isn't one of these internal codes, so callers
 * can fall back to showing the content as-is.
 */
export function decodeInteractiveReplyCode(
  raw: string | undefined | null,
  translate: (key: string) => string,
): string | undefined {
  const value = (raw || "").trim();
  if (!value) {
    return undefined;
  }
  const upper = value.toUpperCase();
  if (upper.startsWith("PAYMENT:")) {
    return translate("TEAM_INBOX.CONVERSATION.INTERACTIVE_PAYMENT_CHOICE");
  }
  if (upper.startsWith("DELIVERY:")) {
    return translate("TEAM_INBOX.CONVERSATION.INTERACTIVE_DELIVERY_CHOICE");
  }
  if (upper.startsWith("PROD:")) {
    return translate("TEAM_INBOX.CONVERSATION.INTERACTIVE_PRODUCT_CHOICE");
  }
  if (upper.startsWith("CAT:")) {
    return translate("TEAM_INBOX.CONVERSATION.INTERACTIVE_CATEGORY_CHOICE");
  }
  if (upper.startsWith("OPT:")) {
    return translate("TEAM_INBOX.CONVERSATION.INTERACTIVE_OPTION_CHOICE");
  }
  if (upper.startsWith("ROOM:")) {
    return translate("TEAM_INBOX.CONVERSATION.INTERACTIVE_ROOM_CHOICE");
  }
  if (
    upper.startsWith("PROD_CONFIRM:") ||
    upper.startsWith("ORDER_CONFIRM:") ||
    upper.startsWith("ADDR_CONFIRM:")
  ) {
    return translate(
      upper.endsWith(":NO")
        ? "TEAM_INBOX.CONVERSATION.INTERACTIVE_NO"
        : "TEAM_INBOX.CONVERSATION.INTERACTIVE_YES",
    );
  }
  return undefined;
}
